// ============================================
// TextbookUpload.jsx - Textbook Upload & Management
// ============================================
// Component for uploading textbooks and viewing library

import React, { useState, useEffect, useRef } from 'react';
import {
  Upload,
  BookOpen,
  FileText,
  Loader,
  Check,
  AlertCircle,
  Trash2,
  Search,
  X,
  ChevronRight,
} from 'lucide-react';
import { textbookService, pollTextbookStatus } from '../services/textbookService';

// ============================================
// FILE UPLOAD ZONE
// ============================================
function UploadZone({ onUpload, isUploading }) {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragIn = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOut = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type === 'application/pdf') {
        onUpload(file);
      }
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      onUpload(file);
    }
  };

  return (
    <div
      className={`upload-zone ${isDragging ? 'dragging' : ''} ${isUploading ? 'uploading' : ''}`}
      onDragEnter={handleDragIn}
      onDragLeave={handleDragOut}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      onClick={() => !isUploading && fileInputRef.current?.click()}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
      
      {isUploading ? (
        <>
          <Loader className="upload-icon spinning" size={40} />
          <p className="upload-text">Uploading...</p>
        </>
      ) : (
        <>
          <Upload className="upload-icon" size={40} />
          <p className="upload-text">
            Drop a PDF here or <span className="upload-link">browse</span>
          </p>
          <p className="upload-hint">Supports PDF textbooks up to 100MB</p>
        </>
      )}
    </div>
  );
}

// ============================================
// TEXTBOOK CARD
// ============================================
function TextbookCard({ textbook, onSelect, onDelete }) {
  const statusColors = {
    ready: 'green',
    processing: 'yellow',
    failed: 'red',
    pending: 'gray',
  };

  const statusIcons = {
    ready: <Check size={14} />,
    processing: <Loader size={14} className="spinning" />,
    failed: <AlertCircle size={14} />,
    pending: <Loader size={14} />,
  };

  return (
    <div className={`textbook-card ${textbook.status}`} onClick={() => textbook.status === 'ready' && onSelect?.(textbook)}>
      <div className="textbook-icon">
        <BookOpen size={24} />
      </div>
      
      <div className="textbook-info">
        <h4 className="textbook-title">{textbook.title}</h4>
        <div className="textbook-meta">
          {textbook.pages && <span>{textbook.pages} pages</span>}
          {textbook.chunks && <span>{textbook.chunks} chunks</span>}
        </div>
      </div>

      <div className={`textbook-status ${statusColors[textbook.status]}`}>
        {statusIcons[textbook.status]}
        <span>{textbook.status}</span>
      </div>

      {textbook.status === 'ready' && (
        <ChevronRight className="textbook-arrow" size={20} />
      )}

      {textbook.status === 'failed' && (
        <button 
          className="textbook-delete"
          onClick={(e) => {
            e.stopPropagation();
            onDelete?.(textbook);
          }}
        >
          <Trash2 size={16} />
        </button>
      )}
    </div>
  );
}

// ============================================
// PROCESSING STATUS
// ============================================
function ProcessingStatus({ textbook, onComplete }) {
  const [status, setStatus] = useState(textbook);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    if (textbook.status !== 'processing') return;

    const cancel = pollTextbookStatus(
      textbook.id,
      (data) => {
        setStatus(data);
        if (data.progress) setProgress(data.progress);
      },
      (data) => {
        setStatus(data);
        onComplete?.(data);
      },
      (error) => {
        setStatus({ ...textbook, status: 'failed', error: error.error });
      }
    );

    return () => cancel();
  }, [textbook.id]);

  return (
    <div className="processing-status">
      <div className="processing-header">
        <Loader className="spinning" size={20} />
        <span>Processing: {status.title}</span>
      </div>
      
      {progress && (
        <div className="processing-progress">
          <div className="progress-bar">
            <div 
              className="progress-fill"
              style={{ width: `${(progress.progress || 0) * 100}%` }}
            />
          </div>
          <span className="progress-step">{progress.step}</span>
        </div>
      )}
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================
export default function TextbookUpload({ onSelectTextbook }) {
  const [textbooks, setTextbooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [processingBooks, setProcessingBooks] = useState([]);

  // Load textbooks on mount
  useEffect(() => {
    loadTextbooks();
  }, []);

  const loadTextbooks = async () => {
    setLoading(true);
    const result = await textbookService.listTextbooks();
    
    if (result.error) {
      setError(result.error);
    } else {
      setTextbooks(result.data?.textbooks || []);
      // Track processing books
      const processing = (result.data?.textbooks || []).filter(t => t.status === 'processing');
      setProcessingBooks(processing);
    }
    setLoading(false);
  };

  const handleUpload = async (file) => {
    setUploading(true);
    setError(null);

    const result = await textbookService.uploadTextbook(file, file.name);

    setUploading(false);

    if (result.error) {
      setError(result.error);
      return;
    }

    // Add to list
    const newTextbook = {
      id: result.data.textbook_id,
      title: result.data.title,
      status: result.data.status,
    };

    setTextbooks((prev) => [newTextbook, ...prev]);

    // If processing async, add to processing list
    if (result.status === 202) {
      setProcessingBooks((prev) => [newTextbook, ...prev]);
    }
  };

  const handleProcessingComplete = (textbook) => {
    // Update textbook in list
    setTextbooks((prev) =>
      prev.map((t) => (t.id === textbook.id ? textbook : t))
    );
    // Remove from processing list
    setProcessingBooks((prev) => prev.filter((t) => t.id !== textbook.id));
  };

  const handleDelete = async (textbook) => {
    const result = await textbookService.deleteTextbook(textbook.id);
    if (!result.error) {
      setTextbooks((prev) => prev.filter((t) => t.id !== textbook.id));
    }
  };

  return (
    <div className="textbook-upload">
      <h3>
        <BookOpen size={20} />
        Textbook Library
      </h3>
      <p className="section-subtitle">
        Upload textbooks to use as reference material for your writing
      </p>

      {/* Upload Zone */}
      <UploadZone onUpload={handleUpload} isUploading={uploading} />

      {/* Error Message */}
      {error && (
        <div className="upload-error">
          <AlertCircle size={16} />
          <span>{error}</span>
          <button onClick={() => setError(null)}>
            <X size={14} />
          </button>
        </div>
      )}

      {/* Processing Status */}
      {processingBooks.map((book) => (
        <ProcessingStatus
          key={book.id}
          textbook={book}
          onComplete={handleProcessingComplete}
        />
      ))}

      {/* Textbook List */}
      <div className="textbook-list">
        {loading ? (
          <div className="loading-state">
            <Loader className="spinning" size={24} />
            <span>Loading textbooks...</span>
          </div>
        ) : textbooks.length === 0 ? (
          <div className="empty-state">
            <FileText size={32} />
            <p>No textbooks uploaded yet</p>
          </div>
        ) : (
          textbooks
            .filter((t) => !processingBooks.find((p) => p.id === t.id))
            .map((textbook) => (
              <TextbookCard
                key={textbook.id}
                textbook={textbook}
                onSelect={onSelectTextbook}
                onDelete={handleDelete}
              />
            ))
        )}
      </div>

      <style jsx>{`
        .textbook-upload {
          padding: 0;
        }

        .textbook-upload h3 {
          display: flex;
          align-items: center;
          gap: 8px;
          margin: 0 0 8px 0;
          color: var(--gi-gray-100, #f3f4f6);
          font-size: 16px;
        }

        .section-subtitle {
          color: var(--gi-gray-500, #6b7280);
          font-size: 13px;
          margin: 0 0 16px 0;
        }

        /* Upload Zone */
        .upload-zone {
          border: 2px dashed var(--gi-gray-700, #374151);
          border-radius: 12px;
          padding: 32px;
          text-align: center;
          cursor: pointer;
          transition: all 0.2s;
          margin-bottom: 16px;
        }

        .upload-zone:hover {
          border-color: var(--gi-green-600, #16a34a);
          background: rgba(22, 163, 74, 0.05);
        }

        .upload-zone.dragging {
          border-color: var(--gi-green-500, #22c55e);
          background: rgba(22, 163, 74, 0.1);
        }

        .upload-zone.uploading {
          pointer-events: none;
          opacity: 0.7;
        }

        .upload-icon {
          color: var(--gi-gray-500, #6b7280);
          margin-bottom: 12px;
        }

        .upload-icon.spinning {
          animation: spin 1s linear infinite;
        }

        .upload-text {
          color: var(--gi-gray-300, #d1d5db);
          margin: 0 0 4px 0;
          font-size: 14px;
        }

        .upload-link {
          color: var(--gi-green-500, #22c55e);
          text-decoration: underline;
        }

        .upload-hint {
          color: var(--gi-gray-600, #4b5563);
          font-size: 12px;
          margin: 0;
        }

        /* Error */
        .upload-error {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px;
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: 8px;
          color: #ef4444;
          font-size: 13px;
          margin-bottom: 16px;
        }

        .upload-error button {
          margin-left: auto;
          background: transparent;
          border: none;
          color: #ef4444;
          cursor: pointer;
          padding: 4px;
        }

        /* Processing Status */
        .processing-status {
          background: var(--gi-gray-800, #1f2937);
          border: 1px solid var(--gi-gray-700, #374151);
          border-radius: 8px;
          padding: 12px;
          margin-bottom: 12px;
        }

        .processing-header {
          display: flex;
          align-items: center;
          gap: 8px;
          color: var(--gi-gray-300, #d1d5db);
          font-size: 13px;
        }

        .processing-progress {
          margin-top: 8px;
        }

        .progress-bar {
          height: 4px;
          background: var(--gi-gray-700, #374151);
          border-radius: 2px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          background: var(--gi-green-600, #16a34a);
          transition: width 0.3s;
        }

        .progress-step {
          display: block;
          margin-top: 4px;
          color: var(--gi-gray-500, #6b7280);
          font-size: 11px;
        }

        /* Textbook List */
        .textbook-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .loading-state,
        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          padding: 32px;
          color: var(--gi-gray-500, #6b7280);
          font-size: 13px;
        }

        /* Textbook Card */
        .textbook-card {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          background: var(--gi-gray-800, #1f2937);
          border: 1px solid var(--gi-gray-700, #374151);
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .textbook-card:hover {
          border-color: var(--gi-gray-600, #4b5563);
        }

        .textbook-card.ready:hover {
          border-color: var(--gi-green-600, #16a34a);
        }

        .textbook-card.failed {
          border-color: rgba(239, 68, 68, 0.3);
          cursor: default;
        }

        .textbook-card.processing {
          cursor: default;
        }

        .textbook-icon {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--gi-gray-700, #374151);
          border-radius: 8px;
          color: var(--gi-gray-400, #9ca3af);
        }

        .textbook-info {
          flex: 1;
          min-width: 0;
        }

        .textbook-title {
          margin: 0;
          color: var(--gi-gray-200, #e5e7eb);
          font-size: 14px;
          font-weight: 500;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .textbook-meta {
          display: flex;
          gap: 12px;
          margin-top: 2px;
          color: var(--gi-gray-500, #6b7280);
          font-size: 12px;
        }

        .textbook-status {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 500;
          text-transform: capitalize;
        }

        .textbook-status.green {
          background: rgba(34, 197, 94, 0.1);
          color: #22c55e;
        }

        .textbook-status.yellow {
          background: rgba(234, 179, 8, 0.1);
          color: #eab308;
        }

        .textbook-status.red {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        }

        .textbook-status.gray {
          background: var(--gi-gray-700, #374151);
          color: var(--gi-gray-400, #9ca3af);
        }

        .textbook-arrow {
          color: var(--gi-gray-500, #6b7280);
        }

        .textbook-delete {
          background: transparent;
          border: none;
          color: var(--gi-gray-500, #6b7280);
          cursor: pointer;
          padding: 8px;
        }

        .textbook-delete:hover {
          color: #ef4444;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .spinning {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
}
